/**
 * Formats a season/episode as a label, e.g. "S01E05".
 */
export function formatEpisodeLabel(episode) {
  if (!episode) return '';
  const season = String(episode.seasonNumber || 0).padStart(2, '0');
  const number = String(episode.episodeNumber || 0).padStart(2, '0');
  return `S${season}E${number}`;
}

/**
 * Returns true if the given episode has been confirmed by an admin.
 * Reads the `confirmed` boolean field stored directly on the episode sub-document.
 * @param {object} episode - episode object with a `confirmed` field
 */
export function isEpisodeConfirmed(episode) {
  if (episode?.missing === true) return false;
  if (episode?.confirmed === true) return true;

  const legacyConfirmedEpisodes = arguments.length > 1 ? arguments[1] : [];
  if (!Array.isArray(legacyConfirmedEpisodes) || legacyConfirmedEpisodes.length === 0) return false;

  const seasonNumber = Number(episode?.seasonNumber);
  const episodeNumber = Number(episode?.episodeNumber);
  const targetLabel = formatEpisodeLabel(episode).toUpperCase();

  return legacyConfirmedEpisodes.some((entry) => {
    if (!entry) return false;
    if (typeof entry === 'string') return entry.toUpperCase() === targetLabel;

    const entrySeason = Number(entry.seasonNumber ?? entry.season ?? entry.s);
    const entryEpisode = Number(entry.episodeNumber ?? entry.episode ?? entry.e);
    if (Number.isFinite(entrySeason) && Number.isFinite(entryEpisode)) {
      return entrySeason === seasonNumber && entryEpisode === episodeNumber;
    }

    const entryLabel = String(entry.label || entry.episodeLabel || '').toUpperCase();
    return entryLabel !== '' && entryLabel === targetLabel;
  });
}

/**
 * Returns true if the given episode has been marked as missing by an admin.
 * Reads the `missing` boolean field stored directly on the episode sub-document.
 * @param {object} episode - episode object with a `missing` field
 */
export function isEpisodeMissing(episode) {
  if (episode?.missing === true) return true;

  const legacyMissingEpisodes = arguments.length > 1 ? arguments[1] : [];
  if (!Array.isArray(legacyMissingEpisodes) || legacyMissingEpisodes.length === 0) return false;

  const seasonNumber = Number(episode?.seasonNumber);
  const episodeNumber = Number(episode?.episodeNumber);
  const targetLabel = formatEpisodeLabel(episode).toUpperCase();

  return legacyMissingEpisodes.some((entry) => {
    if (!entry) return false;
    if (typeof entry === 'string') return entry.toUpperCase() === targetLabel;

    const entrySeason = Number(entry.seasonNumber ?? entry.season ?? entry.s);
    const entryEpisode = Number(entry.episodeNumber ?? entry.episode ?? entry.e);
    if (Number.isFinite(entrySeason) && Number.isFinite(entryEpisode)) {
      return entrySeason === seasonNumber && entryEpisode === episodeNumber;
    }

    const entryLabel = String(entry.label || entry.episodeLabel || '').toUpperCase();
    return entryLabel !== '' && entryLabel === targetLabel;
  });
}

/**
 * Formats an air date value (Firestore Timestamp or string) as a localised date string.
 */
export function formatAirDate(airDate) {
  if (!airDate) return '';
  const d = airDate.toDate ? airDate.toDate() : new Date(airDate);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Returns today's date as an ISO "YYYY-MM-DD" string.
 * Using ISO string comparison is safe because TMDB date fields are stored in the same format.
 */
export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Converts a date value (string or Firestore Timestamp) to a JavaScript Date.
 */
export function toJsDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate(); // Firestore Timestamp
  return new Date(value);
}

/**
 * Returns true if the item's releaseDate is strictly in the future (i.e. "coming soon").
 * Handles both ISO string dates (for movies) and Firestore Timestamps (for TV shows).
 */
export function isComingSoon(item) {
  if (!item.releaseDate) return false;
  const dateStr = typeof item.releaseDate === 'string'
    ? item.releaseDate
    : item.releaseDate.toDate().toISOString().slice(0, 10);
  return dateStr > todayISO();
}

/**
 * Returns the computed display status for an item based on its `confirmed` flag and dates.
 * The `confirmed` boolean is stored on each movie doc and TV show doc by an admin.
 * All other statuses are derived dynamically from the item's release date fields.
 *
 * Movies:
 *   - 'missing'           : confirmed by admin AND marked as missing (file unavailable)
 *   - 'catalogue'         : confirmed by admin (item is available)
 *   - 'coming_soon'       : not confirmed AND before cinema release date (releaseDate)
 *   - 'in_theatres'       : not confirmed AND after cinema release, before streaming/dvd
 *   - 'pending_catalogue' : not confirmed AND streaming or dvd date has passed
 *
 * TV shows:
 *   - 'catalogue'         : confirmed by admin
 *   - 'coming_soon'       : not confirmed AND before first air date (releaseDate)
 *   - 'pending_catalogue' : not confirmed AND air date has passed
 */
export function getItemStatus(item) {
  if (item.missing) return 'missing';

  // Admin-confirmed status always wins (supports both new `confirmed` field and legacy `status`)
  if (item.confirmed || item.status === 'catalogue') {
    return 'catalogue';
  }

  const now = new Date();

  if (item.type === 'movie') {
    const cinemaDate = toJsDate(item.releaseDate);
    const streamingDate = toJsDate(item.streamingReleaseDate);
    const dvdDate = toJsDate(item.dvdReleaseDate);

    if (!cinemaDate || cinemaDate > now) return 'coming_soon';

    const isStreamingOut = streamingDate && streamingDate <= now;
    const isDvdOut = dvdDate && dvdDate <= now;

    if (isStreamingOut || isDvdOut) return 'pending_catalogue';
    return 'in_theatres';
  }

  if (item.type === 'tv') {
    const airDate = toJsDate(item.releaseDate);
    if (!airDate || airDate > now) return 'coming_soon';
    return 'pending_catalogue';
  }

  return item.status || 'coming_soon';
}
